import type { InfiniteData } from '@tanstack/react-query';

import type { BucketContainer, PlayListContainer } from '../types';

import { isValidElement } from './utility';

import type { Bucket, UefatvResponse, PlaylistItem } from '@shared-types/uefatv.types';
import { BucketType } from '@shared-types/uefatv.types';
import { mapEditorialContent } from '@utilities/uefatv';
/**
 * Transforms the data returned from the useInfiniteQuery hook by filtering the `buckets` and `heroes` properties.
 * The `buckets` property is filtered to only include buckets of type `BucketType.VOD_VIDEO` or `BucketType.PLAYLISTS`.
 * The `heroes` property is filtered to only include heroes with a link of type `MediaType.PLAYLIST`, `MediaType.VOD`, or `LinkType.EXTERNALLINK`.
 * @param data - The data returned from the useInfiniteQuery hook.
 * @returns The transformed data with the filtered `buckets` and `heroes` properties.
 */
export const contentSelect = (data: InfiniteData<Partial<UefatvResponse>, string>) =>
  data?.pages?.flatMap((page) => [
    {
      ...page,
      buckets: page?.buckets.filter((b) => [BucketType.VOD_VIDEO, BucketType.PLAYLISTS].includes(b?.type)),
      heroes: page?.heroes.filter((b) => isValidElement(b.link)),
    },
  ]);
/**
 * Retrieves the next page parameter for the infinite query.
 * @param lastPage - The last page of the UefatvResponse data.
 * @returns The `exid` of the last bucket in the last page, or `undefined` if there is no more data available.
 */
export const getNextPageRule = (lastPage: Partial<UefatvResponse> | Partial<Bucket> | Partial<PlaylistItem>) => {
  if (!lastPage?.paging?.moreDataAvailable) return undefined;
  return lastPage?.paging?.lastSeen;
};

export const transformBucketData = (data: InfiniteData<Partial<Bucket>, string>): BucketContainer => ({
  ...data?.pages.at(0),
  items: data?.pages.flatMap((p, i) => p.contentList.map((c) => mapEditorialContent(c, data?.pageParams[i]))),
});

export const transformPlaylistData = (data: InfiniteData<Partial<PlaylistItem>, string>): PlayListContainer => ({
  ...data?.pages.at(0),
  items: data?.pages.flatMap((p) => p.vods).map((c) => mapEditorialContent(c)),
});
